import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { message, Modal } from "antd";

import useCompany from "features/company/hooks/useCompany";
import useOptions from "features/masterData/hooks/useOptions";
import * as masterDataServices from "features/masterData/services/optionsApi";
import { useTranslation } from "react-i18next";
import { requestRemoveDat } from "utils/formatter/formatCommon";
import { generateRandomString } from "utils/helper";
import { NATIONALITY } from "../schemas/employeeCreateSchema";
import * as services from "../services/employeeApi";
import * as slices from "../slices/employeeSlice";
import { setIsLoading } from "../slices/employeeSlice";
import { Alert } from "components/elements";

function daytoString(values) {
  const originalDate = new Date(values.$d);

  // Extract year, month, and day components
  const year = originalDate.getFullYear();
  const month = (originalDate.getMonth() + 1).toString().padStart(2, "0"); // Months are zero-indexed, so add 1
  const day = originalDate.getDate().toString().padStart(2, "0");

  // Format the components into the desired string
  const formattedDate = `${year}-${month}-${day}`;

  return formattedDate;
}

const toParams = (values = {}) => {
  const modifiedValues = {
    ...values,
    name: values.name,
    surName: values.surName,
    nameEn: values.nameEn,
    surNameEn: values.surNameEn,
    passportNo: values.passportNo || undefined,
    employeeNationality:
      values.employeeNationality === NATIONALITY.DOMESTIC ? "D" : "F",
    passportIssueDate: values.passportIssueDate
      ? daytoString(values.passportIssueDate)
      : undefined,
    passportExpireDate: values.passportExpireDate
      ? daytoString(values.passportExpireDate)
      : undefined,
    visaIssueDate: values.visaIssueDate
      ? daytoString(values.visaIssueDate)
      : undefined,
    visaExpireDate: values.visaExpireDate
      ? daytoString(values.visaExpireDate)
      : undefined,
    workPermitIssueDate: values.workPermitIssueDate
      ? daytoString(values.workPermitIssueDate)
      : undefined,
    workPermitExpireDate: values.workPermitExpireDate
      ? daytoString(values.workPermitExpireDate)
      : undefined,
    identityNoOrTaxId: values.taxId,
    taxId:
      values.employeeNationality === NATIONALITY.DOMESTIC ? values.taxId : "",
    workPermitNo: values.workPermitNo || "",
  };

  return modifiedValues;
};

function useEmployeeCreate() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.employee.isLoading);
  const currentCompany = useSelector((state) => state.company.currentCompany);

  const { companyList = [] } = useCompany();
  const {
    employeeTypeOptions = [],
    titleNameOptions = [],
    titleNameEnOptions = [],
    getEmployeeTypeOptions,
    getTitleEnOptions,
    getTitleOptions,
  } = useOptions();

  const onEdit = () => { };
  const { i18n, t } = useTranslation();
  // const { organizationUnderOptions = [], getDepartment } = useOrganization();
  // const { modifiedPositionOptions = [], getPosition } = usePosition(onEdit);

  const {
    departmentOptions,
    positionOptions,
  } = useSelector((state) => ({
    departmentOptions: state.employee.departmentOptions,
    positionOptions: state.employee.positionOptions,
  }));

  // * Country options (ประเทศที่ออกหนังสือเดินทาง / ประเทศที่ออกวีซ่า)
  const [countryOptions, setCountryOptions] = useState([]);

  const getCountryOptions = useCallback(async () => {
    try {
      dispatch(setIsLoading(true));
      const values = {
        requestId: generateRandomString(),
      };
      const response = await masterDataServices.getCountryOptions(values);
      setCountryOptions(response.data);
    } catch (error) {
      Alert({ type: "error", resultObject: error });
    } finally {
      dispatch(setIsLoading(false));
    }
  }, [dispatch]);

  // * Visa type options (ประเภทวีซ่า)
  // * ข้อมูลนี้อยู่ใน MstCommonType (global master data) ไม่ใช่ MstCommonTypeCompany
  // * เรียกผ่าน service.getCommonTypeByGroupType (endpoint /v1/dropdown/getCommonTypeByGroupType)
  const [visaTypeOptions, setVisaTypeOptions] = useState([]);

  const getVisaTypeOptions = useCallback(async () => {
    try {
      dispatch(setIsLoading(true));
      const response = await masterDataServices.getVisaType();
      setVisaTypeOptions(response.data);
    } catch (error) {
      Alert({ type: "error", resultObject: error });
    } finally {
      dispatch(setIsLoading(false));
    }
  }, [dispatch]);

  const modifiedOptions = (options) =>
    options?.map((opt) => ({
      ...opt,
      label: i18n.language === "en" ? (opt?.nameEn ? opt?.nameEn : opt.name) : opt.name,
      value: opt.id,
    }));

  const modifiedCountryOptions = useMemo(
    () => modifiedOptions(countryOptions),
    [countryOptions, i18n.language]
  );

  const modifiedVisaTypeOptions = useMemo(
    () => modifiedOptions(visaTypeOptions),
    [visaTypeOptions, i18n.language]
  );

  const useFetchOptions = (serviceFunction, companyId, groupType, dispatchFunction) => {
    const dispatch = useDispatch();
    useEffect(() => {
      const fetchData = async () => {
        dispatch(slices.setIsLoading(true));
        try {
          const response = await serviceFunction({
            requestId: generateRandomString(),
            companyId,
            groupType,
          });
          dispatch(dispatchFunction(response?.data));
        } catch (error) {
          console.error('Failed to fetch data:', error);
        } finally {
          dispatch(slices.setIsLoading(false));
        }
      };

      if (companyId) {
        fetchData();
      }
    }, [companyId, serviceFunction, groupType, dispatchFunction, dispatch]);
  };


  useFetchOptions(services.getDepartmentOptions, currentCompany, "", slices.setDepartmentOptions);
  useFetchOptions(services.getPositionOptions, currentCompany, "", slices.setPositionOptions);

  const modifiedDepartmentOptions = useMemo(() => modifiedOptions(departmentOptions), [departmentOptions, i18n.language]);
  const modifiedPositionOptions = useMemo(() => modifiedOptions(positionOptions), [positionOptions, i18n.language]);

  const createEmployee = useCallback(
    async (values) => {
      try {
        dispatch(setIsLoading(true));
        let res = await services.createEmployee(
          toParams({
            ...values,
            employeeImage: values?.avatar,
            requestId: generateRandomString(),
            companyId: currentCompany,
            taxId: values?.taxId ? requestRemoveDat(values?.taxId) : null
          })
        );
        if (res.data?.content.code == 200) {
          message.success(t("common.success"))
          navigate("/employee");
        } else {
          function contentDetail(params) {
            return res?.data?.content?.details?.map?.(o => {
              return <div>{o?.key} {o?.value}</div>
            })
          }
          Modal.error({ content: (contentDetail()), okText: t("confirm") })
        }

      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, dispatch, navigate]
  );

  const onSubmit = useCallback(
    (values) => {
      Modal.confirm({
        title: t('confirmation-1'),
        content: (
          <>
            <p>{t('are-you-sure-confirm-to-create-employee')}</p>
          </>
        ),
        onOk() {
          createEmployee(values);
        },
        okText: t('logout.confirm'),
        cancelText: t('reset.cancel'),
      });
    },
    [createEmployee]
  );

  const onCancel = useCallback(
    (values) => {
      navigate("/employee");
    },
    [navigate]
  );

  // useEffect(() => {
  //   if (currentCompany != null) {
  //     // getDepartment();
  //     // getPosition({ page: false });
  //   }
  // }, [currentCompany, getDepartment]);

  useEffect(() => {
    getTitleOptions();
    getTitleEnOptions();
  }, [getTitleOptions, getTitleEnOptions]);

  useEffect(() => {
    if (currentCompany != null) {
      getEmployeeTypeOptions({
        companyId: currentCompany,
        groupType: "EMPLOYEE_TYPE",
      });
    }
  }, [currentCompany, getEmployeeTypeOptions]);

  useEffect(() => {
    getCountryOptions();
  }, [getCountryOptions]);

  useEffect(() => {
    if (currentCompany != null) {
      getVisaTypeOptions();
    }
  }, [currentCompany, getVisaTypeOptions]);

  return {
    isLoading,
    companyList,
    titleNameOptions,
    titleNameEnOptions,
    employeeTypeOptions,
    modifiedPositionOptions,
    organizationUnderOptions: modifiedDepartmentOptions,
    countryOptions: modifiedCountryOptions,
    visaTypeOptions: modifiedVisaTypeOptions,
    onCancel,
    onSubmit,
  };
}

export default useEmployeeCreate;